import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from './ui/dropdown-menu';
import { FileDown, Loader2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const STAGE_LABEL = {
  idle: 'Generate report',
  queued: 'Queued…',
  generating: 'Generating…',
  completed: 'Ready — downloading',
  failed: 'Failed',
};

/**
 * Reusable "Generate report" button.
 *
 * Props:
 *   - options: [{ key, label, report_type, framework_id? }]
 *     If length === 1, renders a plain button; otherwise a split dropdown.
 *   - variant, size, className: passthrough to <Button>
 *   - testid: base data-testid (defaults to 'generate-report-btn')
 *
 * On click: POST /api/report-jobs → polls every 1.5s → auto-downloads the
 * completed PDF as a blob. Errors surface as toasts and reset the state.
 */
export function GenerateReportButton({
  options,
  variant = 'default',
  size = 'default',
  className = '',
  testid = 'generate-report-btn',
}) {
  const [stage, setStage] = useState('idle');
  const [activeLabel, setActiveLabel] = useState(null);
  const pollTimerRef = useRef(null);

  useEffect(() => () => { if (pollTimerRef.current) clearTimeout(pollTimerRef.current); }, []);

  const runGeneration = async (opt) => {
    if (stage !== 'idle' && stage !== 'failed' && stage !== 'completed') return;
    setStage('queued');
    setActiveLabel(opt.label);
    try {
      const payload = { report_type: opt.report_type };
      if (opt.framework_id) payload.framework_id = opt.framework_id;
      const res = await axios.post(`${API}/report-jobs`, payload);
      const jobId = res.data.id;
      toast.success(`Queued: ${res.data.label}`);
      pollJob(jobId, opt.label);
    } catch (e) {
      setStage('failed');
      toast.error(e.response?.data?.detail || 'Failed to queue report');
      setTimeout(() => setStage('idle'), 3000);
    }
  };

  const pollJob = (jobId, label) => {
    const tick = async () => {
      try {
        const res = await axios.get(`${API}/report-jobs/${jobId}`);
        const s = res.data.status;
        if (s === 'generating') setStage('generating');
        if (s === 'completed') {
          setStage('completed');
          await downloadJob(jobId, res.data.label || label);
          setTimeout(() => { setStage('idle'); setActiveLabel(null); }, 2500);
          return;
        }
        if (s === 'failed') {
          setStage('failed');
          toast.error(`Report failed: ${res.data.error || 'unknown error'}`);
          setTimeout(() => { setStage('idle'); setActiveLabel(null); }, 3500);
          return;
        }
        pollTimerRef.current = setTimeout(tick, 1500);
      } catch (e) {
        setStage('failed');
        toast.error('Lost track of report job');
        setTimeout(() => setStage('idle'), 3000);
      }
    };
    tick();
  };

  const downloadJob = async (jobId, label) => {
    try {
      const r = await axios.get(`${API}/report-jobs/${jobId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(label || 'report').replace(/[^A-Za-z0-9_\- ]/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Download failed');
    }
  };

  const busy = stage !== 'idle';
  const Icon = busy ? Loader2 : FileDown;
  const label = busy ? `${STAGE_LABEL[stage]}${activeLabel ? ` · ${activeLabel}` : ''}` : (options.length === 1 ? options[0].label : STAGE_LABEL.idle);

  if (options.length === 1) {
    return (
      <Button
        variant={variant}
        size={size}
        className={className}
        disabled={busy}
        onClick={() => runGeneration(options[0])}
        data-testid={testid}
      >
        <Icon className={`w-4 h-4 mr-1.5 ${busy && stage !== 'completed' && stage !== 'failed' ? 'animate-spin' : ''}`} />
        {label}
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={className}
          disabled={busy}
          data-testid={testid}
        >
          <Icon className={`w-4 h-4 mr-1.5 ${busy && stage !== 'completed' && stage !== 'failed' ? 'animate-spin' : ''}`} />
          {label}
          {!busy && <ChevronDown className="w-4 h-4 ml-1" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {options.map((opt) => (
          <DropdownMenuItem
            key={opt.key}
            onSelect={() => runGeneration(opt)}
            data-testid={`${testid}-option-${opt.key}`}
          >
            <FileDown className="w-4 h-4 mr-2" /> {opt.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default GenerateReportButton;
